// /admin/rules/:ruleId — Rule detail page.
// Uses <EntityHeader> + <RelationshipTabs> with four tabs:
// Overview, Users, Group Members, Activity.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import {
  Button,
  EntityHeader,
  Modal,
  RelationshipTabs,
  StatusChip,
  Table,
  useToast,
  type TableColumn,
} from "@/components";
import { ruleClient } from "@/data/clients/ruleClient";
import {
  AdminApiError,
  classifyAdminError,
} from "@/data/httpClient";
import { useRole, useCapabilities } from "@/role";
import type { Rule, RuleUser, GroupMember } from "@/types/rule";
import type { AuditEntry } from "@/types/role";
import "../AdminShared.css";

interface RuleDetail extends Rule {
  users_count: number;
  group_member_count: number;
}

export function RuleDetailPage(): JSX.Element {
  const { ruleId } = useParams<{ ruleId: string }>();
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const toast = useToast();
  const { currentRole, scopedFetchToken } = useRole();
  const caps = useCapabilities();
  const canManage = caps.canManageRules;

  const [rule, setRule] = useState<RuleDetail | null>(null);
  const [loading, setLoading] = useState(true);

  // Sub-resource state
  const [users, setUsers] = useState<RuleUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([]);
  const [groupMembersLoading, setGroupMembersLoading] = useState(false);
  const [activity, setActivity] = useState<AuditEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Track which sub-resources have been loaded to avoid re-fetching
  const loadedTabs = useRef<Set<string>>(new Set());

  // Derive active tab from URL search params
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "overview";

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const loadRule = useCallback(async () => {
    if (!ruleId) return;
    setLoading(true);
    try {
      const data = await ruleClient.get(ruleId);
      setRule(data as RuleDetail);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 404) {
          navigate("/admin/rules", { replace: true });
          toast.danger({ title: "Rule not found" });
        } else {
          handleApiError(err, { actionLabel: `Load rule ${ruleId}` });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [ruleId, navigate, toast, handleApiError]);

  useEffect(() => {
    if (currentRole === null) return;
    void loadRule();
  }, [currentRole, scopedFetchToken, loadRule]);

  // Load users
  const loadUsers = useCallback(async () => {
    if (!ruleId) return;
    setUsersLoading(true);
    try {
      const resp = await ruleClient.listUsers(ruleId);
      setUsers(resp.items);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Load rule users" });
      }
    } finally {
      setUsersLoading(false);
    }
  }, [ruleId, handleApiError]);

  // Load group members
  const loadGroupMembers = useCallback(async () => {
    if (!ruleId) return;
    setGroupMembersLoading(true);
    try {
      const resp = await ruleClient.listGroupMembers(ruleId);
      setGroupMembers(resp.items);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Load group members" });
      }
    } finally {
      setGroupMembersLoading(false);
    }
  }, [ruleId, handleApiError]);

  // Load activity
  const loadActivity = useCallback(async () => {
    if (!ruleId) return;
    setActivityLoading(true);
    try {
      const resp = await ruleClient.listActivity(ruleId);
      setActivity(resp.items);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Load rule activity" });
      }
    } finally {
      setActivityLoading(false);
    }
  }, [ruleId, handleApiError]);

  // Lazy-load sub-resources when their tab becomes active
  useEffect(() => {
    if (!rule) return;
    if (activeTab === "users" && !loadedTabs.current.has("users")) {
      loadedTabs.current.add("users");
      void loadUsers();
    } else if (activeTab === "group-members" && !loadedTabs.current.has("group-members")) {
      loadedTabs.current.add("group-members");
      void loadGroupMembers();
    } else if (activeTab === "activity" && !loadedTabs.current.has("activity")) {
      loadedTabs.current.add("activity");
      void loadActivity();
    }
  }, [activeTab, rule, loadUsers, loadGroupMembers, loadActivity]);

  // Delete handler
  const handleDelete = useCallback(async () => {
    if (!rule) return;
    setDeleteSubmitting(true);
    try {
      const result = await ruleClient.delete(rule.rule_id, rule.version);
      const removed = result.summary?.grants_removed ?? 0;
      toast.success({
        title: `Deleted rule "${rule.display_name}"`,
        description: removed > 0 ? `${removed} grants removed.` : undefined,
      });
      setShowDeleteModal(false);
      navigate("/admin/rules", { replace: true });
    } catch (err) {
      setShowDeleteModal(false);
      if (err instanceof AdminApiError) {
        if (err.typed.kind === "version_mismatch") {
          toast.warning({
            title: "Version conflict",
            description:
              "Someone else edited this rule. Please reload and try again.",
          });
        } else {
          toast.danger({ title: err.message });
        }
      } else if (err instanceof ApiError) {
        const typed = classifyAdminError(err);
        if (typed && typed.typed.kind === "version_mismatch") {
          toast.warning({
            title: "Version conflict",
            description:
              "Someone else edited this rule. Please reload and try again.",
          });
        } else {
          handleApiError(err, {
            actionLabel: `Delete rule ${rule.display_name}`,
          });
        }
      }
    } finally {
      setDeleteSubmitting(false);
    }
  }, [rule, toast, navigate, handleApiError]);

  // Memo-stable formatters; included in the column-builder useMemo deps so
  // we don't have to inline these. The ESLint rule that warned about this
  // wants stable identities — useCallback gives us that.
  const formatScope = useCallback(
    (scope: { mode: "ALL" | "ALLOW" | "DENY"; ids: string[] }): string => {
      if (scope.mode === "ALL") return "All";
      if (scope.mode === "ALLOW") {
        return scope.ids.length === 0
          ? "(none)"
          : `Allow [${scope.ids.join(", ")}]`;
      }
      if (scope.mode === "DENY") {
        return scope.ids.length === 0
          ? "(none)"
          : `Deny [${scope.ids.join(", ")}]`;
      }
      return "—";
    },
    [],
  );
  const formatLocationScope = useCallback(
    (r: Rule): string => formatScope(r.location_scope),
    [formatScope],
  );
  const formatDeviceScope = useCallback(
    (r: Rule): string => formatScope(r.device_scope),
    [formatScope],
  );

  // User table columns
  const userColumns = useMemo<TableColumn<RuleUser>[]>(
    () => [
      {
        key: "alias",
        header: "Alias",
        render: (u) => <code className="mono">{u.alias}</code>,
      },
      {
        key: "role",
        header: "Role",
        render: () =>
          rule ? <code className="mono">{rule.role_id}</code> : null,
      },
      {
        key: "location_id",
        header: "Location",
        render: (u) => (
          <code className="mono">
            {u.location_id
              ? u.location_id.startsWith("fleet-")
                ? "All locations"
                : u.location_id
              : "All locations"}
          </code>
        ),
      },
      {
        key: "granted_at",
        header: "Granted at",
        render: (u) => (
          <span className="admin-muted">
            {u.granted_at
              ? new Date(u.granted_at).toLocaleDateString()
              : "—"}
          </span>
        ),
      },
    ],
    [rule],
  );

  // Group member table columns
  const groupMemberColumns = useMemo<TableColumn<GroupMember>[]>(
    () => [
      {
        key: "alias",
        header: "Alias",
        render: (m) => <code className="mono">{m.alias}</code>,
      },
      {
        key: "ring_user_id",
        header: "Ring user ID",
        render: (m) => (
          <code className="mono">{m.ring_user_id || "—"}</code>
        ),
      },
      {
        key: "group_name",
        header: "Group",
        render: (m) => <code className="mono">{m.group_name}</code>,
      },
      {
        key: "synced_at",
        header: "Synced at",
        render: (m) => (
          <span className="admin-muted">
            {m.synced_at
              ? new Date(m.synced_at).toLocaleDateString()
              : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  // Activity table columns
  const activityColumns = useMemo<TableColumn<AuditEntry>[]>(
    () => [
      {
        key: "timestamp",
        header: "Time",
        render: (e) => (
          <code className="mono" style={{ fontSize: 12 }}>
            {e.timestamp
              ? new Date(e.timestamp).toLocaleString()
              : "—"}
          </code>
        ),
      },
      {
        key: "event_type",
        header: "Event",
        render: (e) => (
          <StatusChip tone="info">
            {e.event_type.replace(/_/g, " ").toLowerCase()}
          </StatusChip>
        ),
      },
      {
        key: "actor",
        header: "Actor",
        render: (e) => <code className="mono">{e.actor}</code>,
      },
      {
        key: "details",
        header: "Details",
        render: (e) => (
          <span className="admin-muted">
            {Object.keys(e.details).length > 0
              ? JSON.stringify(e.details)
              : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  // Build tabs
  const tabs = useMemo(() => {
    if (!rule) return [];
    return [
      {
        id: "overview",
        label: "Overview",
        render: () => (
          <div className="admin-identity" style={{ gap: 16 }}>
            <div className="admin-identity__kv">
              <span className="admin-identity__label">Name</span>
              <span className="admin-identity__value--plain">
                {rule.display_name}
              </span>
            </div>
            <div className="admin-identity__kv">
              <span className="admin-identity__label">Description</span>
              <span className="admin-identity__value--plain">
                {rule.description || "—"}
              </span>
            </div>
            <div className="admin-identity__kv">
              <span className="admin-identity__label">Group match</span>
              <span>
                <code className="admin-identity__value">
                  {rule.group_match
                    ? `${rule.group_match.type}: ${rule.group_match.group_name}`
                    : "—"}
                </code>
              </span>
            </div>
            <div className="admin-identity__kv">
              <span className="admin-identity__label">Role</span>
              <button
                type="button"
                className="rb-link-btn"
                onClick={() =>
                  navigate(
                    `/admin/roles/${encodeURIComponent(rule.role_id)}`,
                  )
                }
              >
                {rule.role_id}
              </button>
            </div>
            <div className="admin-identity__kv">
              <span className="admin-identity__label">Location scope</span>
              <span className="admin-identity__value--plain">
                {formatLocationScope(rule)}
              </span>
            </div>
            <div className="admin-identity__kv">
              <span className="admin-identity__label">Device scope</span>
              <span className="admin-identity__value--plain">
                {formatDeviceScope(rule)}
              </span>
            </div>
            <div className="admin-identity__kv">
              <span className="admin-identity__label">Status</span>
              <span>
                {rule.enabled ? (
                  <StatusChip tone="success">enabled</StatusChip>
                ) : (
                  <StatusChip tone="warning">disabled</StatusChip>
                )}
              </span>
            </div>
            <div className="admin-identity__kv">
              <span className="admin-identity__label">Version</span>
              <code className="admin-identity__value">{rule.version}</code>
            </div>
            <div className="admin-identity__kv">
              <span className="admin-identity__label">Created</span>
              <span className="admin-identity__value--plain">
                {new Date(rule.created_at).toLocaleString()} by{" "}
                {rule.created_by}
              </span>
            </div>
            {rule.updated_at && (
              <div className="admin-identity__kv">
                <span className="admin-identity__label">Last modified</span>
                <span className="admin-identity__value--plain">
                  {new Date(rule.updated_at).toLocaleString()} by{" "}
                  {rule.updated_by ?? "—"}
                </span>
              </div>
            )}
          </div>
        ),
      },
      {
        id: "users",
        label: "Users",
        count: rule.users_count,
        render: () => {
          return (
            <Table<RuleUser>
              columns={userColumns}
              rows={users}
              loading={usersLoading}
              rowKey={(u, i) => `${u.alias}-${u.location_id}-${i}`}
              emptyState="No users currently receive grants from this rule."
            />
          );
        },
      },
      {
        id: "group-members",
        label: "Group Members",
        count: rule.group_member_count,
        render: () => {
          return (
            <Table<GroupMember>
              columns={groupMemberColumns}
              rows={groupMembers}
              loading={groupMembersLoading}
              rowKey={(m, i) => `${m.alias}-${m.group_name}-${i}`}
              emptyState="No members in the matched group."
            />
          );
        },
      },
      {
        id: "activity",
        label: "Activity",
        render: () => {
          return (
            <Table<AuditEntry>
              columns={activityColumns}
              rows={activity}
              loading={activityLoading}
              rowKey={(e) => e.event_id}
              emptyState="No activity recorded."
            />
          );
        },
      },
    ];
  }, [
    rule,
    navigate,
    users,
    usersLoading,
    userColumns,
    groupMembers,
    groupMembersLoading,
    groupMemberColumns,
    activity,
    activityLoading,
    activityColumns,
    formatLocationScope,
    formatDeviceScope,
  ]);

  if (loading) {
    return (
      <section className="admin-page">
        <div className="admin-empty">Loading rule…</div>
      </section>
    );
  }

  if (!rule) {
    return (
      <section className="admin-page">
        <div className="admin-empty">Rule not found.</div>
      </section>
    );
  }

  return (
    <section className="admin-page">
      <EntityHeader
        icon="📋"
        displayName={rule.display_name}
        idLabel={rule.rule_id}
        breadcrumbs={[
          { label: "Rules", to: "/admin/rules" },
          { label: rule.display_name },
        ]}
        statusChips={[
          rule.enabled ? (
            <StatusChip tone="success">enabled</StatusChip>
          ) : (
            <StatusChip tone="warning">disabled</StatusChip>
          ),
        ]}
        actions={
          <>
            <Button
              variant="secondary"
              disabled={!canManage}
              title={!canManage ? "Requires rule:manage permission" : undefined}
              onClick={() =>
                navigate(
                  `/admin/rules/${encodeURIComponent(rule.rule_id)}/edit`,
                )
              }
            >
              Edit
            </Button>
            <Button
              variant="secondary"
              disabled={!canManage}
              title={!canManage ? "Requires rule:manage permission" : undefined}
              onClick={() => setShowDeleteModal(true)}
            >
              Delete
            </Button>
          </>
        }
      />

      <RelationshipTabs tabs={tabs} defaultTabId="overview" />

      {/* Delete confirmation modal */}
      <Modal
        open={showDeleteModal}
        onClose={() => {
          if (!deleteSubmitting) setShowDeleteModal(false);
        }}
        title="Delete rule"
        description={`Are you sure you want to delete "${rule.display_name}"? Users matched by this rule will lose the grants it produces.`}
        variant="confirm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleteSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteSubmitting}
              onClick={() => void handleDelete()}
            >
              Delete
            </Button>
          </>
        }
      />
    </section>
  );
}
